// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Dialog, DialogContent, DialogActions, DialogTitle, IconButton, Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
// Components, Layouts, Pages
import { BaseLabel, BaseSelect } from '~/components/common';
// Others
import { defaultValuesChangeStatus, schema } from './helper';
import { IBaseOption, IResultCallApiFn } from '~/utils/interface/common';
import { IChangeStatusInvoice } from '~/utils/interface/invoice';
import { optionStatusInvoice } from '~/utils/constants/common';
// Styles, images, icons
import styles from './ChangeStatusInvoiceModal.module.scss';
import { icons } from '~/assets';

type Props = {
  status: string;
  onApply: (status: string) => void;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const ChangeStatusInvoiceModal = (props: Props) => {
  //#region Destructuring Props
  const { status, onClose, onApply } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/invoice', 'common/base']);

  const {
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<IChangeStatusInvoice>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesChangeStatus,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    reset({
      status,
    });
  }, [status]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleApply = (body: IChangeStatusInvoice) => {
    const { status } = body;

    onApply(status);
    handleClear();
  };

  const handleClear = () => {
    onClose();
    reset();
  };

  const fetchOptionsStatus = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionStatusInvoice,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Dialog
      aria-labelledby='changeStatusFormModalFormModal'
      open={true}
      onClose={handleClear}
      fullWidth
      maxWidth={'sm'}
      PaperProps={{
        sx: {
          width: 400,
          maxWidth: 400,
        },
      }}
    >
      <DialogTitle id='changeStatusFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel label={t('invoice_modal_change_status_title')} styleType='title' />
          </Stack>
        </Stack>

        <IconButton onClick={handleClear} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={cx('body')}>
        <Controller
          name={'status'}
          control={control}
          render={({ field: { onChange, value } }) => (
            <BaseSelect
              gap={10}
              height={36}
              styleType='contained'
              label={t('invoice_modal_change_status_title')}
              placeholder={t('common_text_placeholder_select', { ns: 'common/base' })}
              callApiFn={fetchOptionsStatus}
              value={value}
              onChange={(data) => onChange(data?.value)}
              errorMessage={errors?.status?.message}
              required
            />
          )}
        />
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleClear}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button variant='contained' className={cx('buttonSave')} onClick={handleSubmit(handleApply)}>
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeStatusInvoiceModal;
